import { Box, Spinner } from "@chakra-ui/react";
import { isAxiosError } from "axios";
import { useParams, useResolvedPath, useSearchParams } from "react-router";

import InvalidRoute from "@/app/pages/InvalidRoute";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import HeaderLayout from "@/common/components/HeaderLayout";
import RouterTabs, {
  Route,
  useActivePathRouteIndex,
} from "@/common/components/RouterTabs";
import { useCompanyData } from "@/company/data/useCompanyData";

const tabSafeParams = (params: URLSearchParams) => {
  params.forEach((_, key) => {
    // Remove any non-filtering params (such as sorting, columns, page, etc.)
    if (!["search", "stars", "satisfaction", "feature"].includes(key)) {
      params.delete(key);
    }
  });
  return params.toString();
};

export default function Company() {
  const [searchParams] = useSearchParams();
  const { companyId } = useParams();
  const safeParams = tabSafeParams(searchParams);

  const {
    data: company,
    error,
    isLoading,
    isSuccess,
  } = useCompanyData(companyId);
  const isNotFound = isAxiosError(error) && error.response?.status === 404;

  const tabsRoutes: Route[] = [
    {
      name: "Flags",
      route: {
        pathname: useResolvedPath("features").pathname,
        search: safeParams,
      },
    },
    {
      name: "Feedback",
      count: company?.feedbackCount,
      countIsLoading: isLoading,
      route: {
        pathname: useResolvedPath("feedback").pathname,
        search: safeParams,
      },
    },
    {
      name: "Users",
      count: company?.userCount,
      countIsLoading: isLoading,
      route: {
        pathname: useResolvedPath("users").pathname,
        search: safeParams,
      },
    },
    {
      name: "Attributes",
      route: {
        pathname: useResolvedPath("attributes").pathname,
        search: safeParams,
      },
    },
  ];

  const activeRouteIndex = useActivePathRouteIndex(tabsRoutes);

  if (isNotFound) {
    return <InvalidRoute />;
  }

  return (
    <HeaderLayout
      title={
        isLoading ? (
          <Box minH={6}>
            <Spinner />
          </Box>
        ) : isSuccess ? (
          <CompanyDisplay
            as="h1"
            company={company}
            fontSize={"lg"}
            fontWeight="medium"
          />
        ) : undefined
      }
      borderless
    >
      <RouterTabs
        activeRouteIndex={activeRouteIndex}
        routes={tabsRoutes}
        tabListProps={{ px: 3 }}
        titleTemplate={`Company › ${company?.name} › %s`}
      />
    </HeaderLayout>
  );
}
